import {
  Battery,
  Clock,
  DollarSign,
  Frown,
  Laugh,
  Lightbulb,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { Badge } from "#app/components/ui/badge.tsx";

type TagType =
  | "crowded"
  | "expensive"
  | "free"
  | "support"
  | "ammenities"
  | "unstable"
  | "fast"
  | "slow";

interface TagData {
  name: TagType;
}

interface EVStationTagsProps {
  tags: TagData[];
}

export default function EVStationTags({ tags }: EVStationTagsProps) {
  const tagIcons: Record<TagType, React.ReactNode> = {
    crowded: <Clock className="h-3 w-3" />,
    expensive: <DollarSign className="h-3 w-3" />,
    free: <Laugh className="h-3 w-3" />,
    support: <ShieldCheck className="h-3 w-3" />,
    ammenities: <Lightbulb className="h-3 w-3" />,
    unstable: <Frown className="h-3 w-3" />,
    fast: <Zap className="h-3 w-3" />,
    slow: <Battery className="h-3 w-3" />,
  };

  const tagLabels: Record<TagType, string> = {
    crowded: "Congestionada",
    expensive: "Caro",
    free: "Gratuito/Barato",
    support: "Ótimo Suporte",
    ammenities: "Ótimas Comodidades",
    unstable: "Instável",
    fast: "Carregamento Rápido",
    slow: "Carregamento Lento",
  };

  const tagColors: Record<TagType, string> = {
    crowded: "bg-red-100 text-red-800 border-red-200",
    expensive: "bg-red-100 text-red-800 border-red-200",
    free: "bg-green-100 text-green-800 border-green-200",
    support: "bg-green-100 text-green-800 border-green-200",
    ammenities: "bg-green-100 text-green-800 border-green-200",
    unstable: "bg-red-100 text-red-800 border-red-200",
    fast: "bg-green-100 text-green-800 border-green-200",
    slow: "bg-red-100 text-red-800 border-red-200",
  };

  if (tags.length === 0) {
    return null; // Se não houver tags, não renderiza nada
  }

  // Separate tags into green and red
  const greenTags = tags.filter(
    (tag) => tagColors[tag.name]?.includes("green") ?? false,
  );
  const redTags = tags.filter(
    (tag) => tagColors[tag.name]?.includes("red") ?? false,
  );

  const renderTags = (tagList: TagData[]) =>
    tagList
      .map((tag) => {
        if (!tagColors[tag.name]) {
          console.log(`Unknown tag: ${tag.name}`);
          return null;
        }
        return (
          <Badge
            key={tag.name}
            variant="outline"
            className={`flex items-center gap-1 ${tagColors[tag.name]}`}
            role="listitem"
          >
            {tagIcons[tag.name]}
            <span className="sr-only">{tagLabels[tag.name]}:</span>
            {tagLabels[tag.name]}
          </Badge>
        );
      })
      .filter(Boolean);

  return (
    <div
      className="flex flex-col gap-2"
      role="list"
      aria-label="Características da estação"
    >
      <div className="flex flex-wrap gap-2">{renderTags(greenTags)}</div>
      <div className="flex flex-wrap gap-2">{renderTags(redTags)}</div>
    </div>
  );
}
